<template>
  <div>
    <div
      class="medication-route-selects el-input-group el-input-group--append el-input-group--prepend"
    >
      <div class="el-input-group__prepend">
        Was
      </div>
      <el-select
        id="medication-group-select"
        v-close-on-zoom
        :value="state.check.group"
        placeholder="Medication"
        filterable
        class="medication-group-select"
        :disabled="readonly"
        @input="medicationGroupId => selectMedicationGroup(medicationGroupId)"
      >
        <el-option
          v-for="medicationGroup in availableMedicationGroups"
          :key="medicationGroup.key"
          :label="medicationGroup.label.en"
          :value="medicationGroup.key"
        />
      </el-select>
      <el-select
        id="routes-select"
        v-close-on-zoom
        :value="state.check.routes"
        placeholder="Any route"
        multiple
        class="routes-select"
        :disabled="readonly"
        @input="routeKeys => selectRoutes(routeKeys)"
      >
        <el-option
          v-for="route in availableRoutes"
          :key="route.key"
          :label="route.label.en"
          :value="route.key"
        />
      </el-select>
      <el-select
        v-close-on-zoom
        :value="state.check.metric"
        :disabled="readonly"
        class="metric-select"
        @input="value => updateMetric(value)"
      >
        <el-option value="GIVEN" :label="'given'" />
        <el-option value="DELIVERED" :label="'delivered'" />
      </el-select>
    </div>
    <div class="dosage-count-check-container">
      <dosage-count-check
        v-if="state.check.group"
        :disabled="readonly"
        :mode="state.check.metric === 'DELIVERED' ? 'DOSAGE' : 'COUNT'"
        :dosage-available="state.check.metric === 'DELIVERED'"
        :count-available="state.check.metric === 'GIVEN'"
        :dosage="state.check.dosage"
        :count="state.check.count"
        :count-operator="state.check.countOperator"
        :units="state.check.unit"
        :units-available="availableUnits"
        class="dosage-count-check"
        @updateDosage="updateDosage"
        @updateCountOperator="updateCountOperator"
        @updateCount="updateCount"
        @updateUnits="updateUnit"
      />
      <help-tooltip v-if="unitDisabledError" type="error">
        <span>{{ unitDisabledError.message }}</span>
      </help-tooltip>
      <help-tooltip
        v-if="!selectedUnitIsAvailableInAllDosageForms"
        type="warning"
      >
        Select a common unit of measurement to look at total administration<br>
        across dosage forms and routes
      </help-tooltip>
    </div>
  </div>
</template>

<script>
import flatten from 'lodash/flatten';
import intersection from 'lodash/intersection';
import keyBy from 'lodash/keyBy';
import uniq from 'lodash/uniq';
import {
  medicationGroups,
  unitTypes,
  dosageForms,
} from '~lib/common/config/medication';
import routes from '~lib/common/config/routes';
import DosageCountCheck from './common/DosageCountCheck.vue';
import HelpTooltip from '~lib/common/components/HelpTooltip';
import medicationCheckMixins from './common/MedicationCheckMixins';

export default {
  name: 'medication-group-check',
  components: {
    DosageCountCheck,
    HelpTooltip,
  },
  mixins: [ medicationCheckMixins ],
  props: [
    'medications',
    'readonly',
    'emitter',
    'ikey',
    'getData',
    'putData',
    'control',
  ],
  data() {
    return {
      state: {
        check: this.getData(this.ikey) || {
          group: null,
          routes: [],
          unit: null,
          dosage: {
            min: null,
            max: null,
          },
          count: null,
          countOperator: null,
          metric: 'DELIVERED',
        },
      },
    };
  },
  computed: {
    medicationsById() {
      const { caseConf, isGlobal } = this.emitter;
      let allMeds;
      if (isGlobal) {
        allMeds = this.medications
      } else {
        allMeds = this.medications.concat(caseConf.customMedications || []);
      }
      return keyBy(allMeds, 'id');
    },
    availableMedicationGroups() {
      const { caseConf, isGlobal } = this.emitter;
      if (isGlobal) {
        return medicationGroups;
      }
      const { dosageForms, customMedGroups } = caseConf;
      if (!dosageForms) {
        return [];
      }
      const allGroups = medicationGroups.concat(customMedGroups || []);
      return uniq(
        dosageForms
          .map(dosageForm => {
            const medication = this.medicationsById[dosageForm.medicationId];
            if (!medication) {
              return null;
            }
            return allGroups.find(group => group.key === medication.group);
          })
          .filter(Boolean),
      );
    },
    availableRoutes() {
      const { caseConf, isGlobal } = this.emitter;
      if (isGlobal) {
        return routes;
      }
      const { dosageForms: caseDosageForms } = caseConf;
      if (!caseDosageForms) {
        return [];
      }
      const selectedDosageForms = caseDosageForms.filter(caseDosageForm => {
        const medication = this.medicationsById[caseDosageForm.medicationId];
        if (!medication) {
          return null;
        }
        return medication.group === this.state.check.group;
      });
      const enabledRouteKeys = flatten(
        selectedDosageForms.map(caseDosageForm =>
          (caseDosageForm.enabledRoutes || []).map(
            enabledRoute => enabledRoute.key,
          ),
        ),
      );
      return routes.filter(route => enabledRouteKeys.includes(route.key));
    },
    availableUnits() {
      const { caseConf, isGlobal } = this.emitter;
      if (isGlobal) {
        return unitTypes.map(unitType => ({
          label: unitType.label.en,
          value: unitType.key
        }));
      }
      const { dosageForms: caseDosageForms } = caseConf;
      if (!caseDosageForms) {
        return [];
      }
      const selectedDosageForms = caseDosageForms.filter(caseDosageForm => {
        const medication = this.medicationsById[caseDosageForm.medicationId];
        if (!medication) {
          return null;
        }
        return (
          medication.group === this.state.check.group &&
          (this.state.check.routes.length === 0 ||
            intersection(
              (caseDosageForm.enabledRoutes || []).map(
                enabledRoute => enabledRoute.key,
              ),
              this.state.check.routes,
            ).length > 0)
        );
      });
      const availableUnitsKeys = flatten(
        selectedDosageForms.map(caseDosageForm => {
          const dosageForm = dosageForms.find(
            df => df.key === caseDosageForm.dosageFormType,
          );
          return [
            caseDosageForm.concentration.numeratorUnits,
            caseDosageForm.concentration.denominatorUnits ||
              (dosageForm && dosageForm.unitType2),
          ];
        }),
      ).filter(Boolean);
      return unitTypes
        .filter(unitType => availableUnitsKeys.includes(unitType.key))
        .map(unitType => ({
          label: unitType.label.en,
          value: unitType.key,
        }));
    },
    selectedUnitIsAvailableInAllDosageForms() {
      const { caseConf, isGlobal } = this.emitter;
      const { unit: checkUnit, group: checkGroup } = this.state.check;
      if (isGlobal) {
        return true;
      }

      const { dosageForms: caseDosageForms } = caseConf;
      if (!caseDosageForms) {
        return true;
      }

      if (!checkGroup || !checkUnit) {
        return true;
      }
      return caseDosageForms
        .filter(caseDosageForm => {
          const medication = this.medicationsById[caseDosageForm.medicationId];
          if (!medication) {
            return false;
          }
          return medication.group === checkGroup;
        })
        .every(caseDosageForm => {
          const dosageForm = dosageForms.find(
            df => df.key === caseDosageForm.dosageFormType,
          );
          return (
            caseDosageForm.concentration.numeratorUnits === checkUnit ||
            (caseDosageForm.concentration.denominatorUnits ||
              dosageForm.unitType2) === checkUnit
          );
        });
    },
  },
  mounted() {
    this.$emit('setQueryContext', {
      type: 'case',
      id: this.emitter.caseConf.id,
    });
    this.$emit('fetch');
  },
  methods: {
    selectMedicationGroup(medicationGroupId) {
      this.state.check.group = medicationGroupId;
      this.selectRoutes([]);
      if (this.state.check.metric === 'DELIVERED') {
        this.updateUnit(this.availableUnits[0].value);
        this.updateDosage({min: null, max: null});
      }
      this.update();
    },
    selectRoutes(routeKeys) {
      this.state.check.routes = routeKeys;
      this.update();
    },
    updateUnit(unit) {
      this.$set(this.state.check, 'unit', unit);
      this.update();
    },
    updateDosage(dosage) {
      this.state.check.dosage = dosage;
      this.update();
    },
    updateCountOperator(operator) {
      this.state.check.countOperator = operator;
      this.update();
    },
    updateCount(count) {
      this.state.check.count = count;
      this.update();
    },
    updateMetric(metric) {
      this.state.check.metric = metric;
      if (metric === 'GIVEN') {
        this.state.check.dosage = null;
        this.state.check.unit = null;
      } else { // 'DELIVERED'
        this.state.check.count = null;
        this.state.check.countOperator = null;
      }
      this.update();
    },
    update() {
      this.putData(this.ikey, {
        ...this.state.check,
        type: 'MEDICATION_GROUP',
      });

      this.emitter.trigger('process', this.control);
    },
  },
};
</script>

<style scoped>
.dosage-count-check-container {
  display: flex;
  flex-direction: row;
  align-items: center;
}
</style>
